import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import { firestore } from "../lib/firebase";
import type {
  ExerciseAnswerRecord,
  ExerciseAnswerStatus,
  ExerciseRuntimeStatus,
  SessionPublicExercise,
} from "../types/access";
import { asRecord, toDate } from "../utils/firestoreData";
import { writeAuditEvent } from "./auditService";

const SESSIONS_COLLECTION = "sessions";
const EXERCISE_ANSWERS_COLLECTION = "exerciseAnswers";
const SESSION_PUBLIC_COLLECTION = "sessionPublic";
const SESSION_RUNTIME_COLLECTION = "sessionRuntime";
const PUBLIC_EXERCISES_COLLECTION = "exercises";

type ExerciseRuntimeTransition = "activate" | "pass" | "publish_correction";

function exerciseAnswerId({
  exerciseRuntimeId,
  uid,
}: {
  exerciseRuntimeId: string;
  uid: string;
}): string {
  return `${uid}_${exerciseRuntimeId}`;
}

function normalizeAnswerStatus(value: unknown): ExerciseAnswerStatus {
  return value === "submitted" ? "submitted" : "draft";
}

function normalizeExerciseAnswer(
  id: string,
  data: Record<string, unknown>,
): ExerciseAnswerRecord {
  return {
    answerMarkdown:
      typeof data.answerMarkdown === "string" ? data.answerMarkdown : "",
    exerciseRuntimeId:
      typeof data.exerciseRuntimeId === "string" ? data.exerciseRuntimeId : "",
    id: typeof data.id === "string" ? data.id : id,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : "",
    sourceExerciseId:
      typeof data.sourceExerciseId === "string" ? data.sourceExerciseId : "",
    startedAt: toDate(data.startedAt),
    status: normalizeAnswerStatus(data.status),
    submittedAt: toDate(data.submittedAt),
    uid: typeof data.uid === "string" ? data.uid : "",
    updatedAt: toDate(data.updatedAt),
    versionId: typeof data.versionId === "string" ? data.versionId : "",
  };
}

const exerciseAnswerConverter: FirestoreDataConverter<ExerciseAnswerRecord> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): ExerciseAnswerRecord {
    return normalizeExerciseAnswer(snapshot.id, asRecord(snapshot.data(options)));
  },
  toFirestore(value: ExerciseAnswerRecord): DocumentData {
    return value;
  },
};

function sessionPublicExerciseRef({
  exerciseRuntimeId,
  sessionId,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_PUBLIC_COLLECTION,
    sessionId,
    PUBLIC_EXERCISES_COLLECTION,
    exerciseRuntimeId,
  );
}

function sessionRuntimeExerciseRef({
  exerciseRuntimeId,
  sessionId,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_RUNTIME_COLLECTION,
    sessionId,
    PUBLIC_EXERCISES_COLLECTION,
    exerciseRuntimeId,
  );
}

function exerciseAnswerRef({
  answerId,
  sessionId,
}: {
  answerId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSIONS_COLLECTION,
    sessionId,
    EXERCISE_ANSWERS_COLLECTION,
    answerId,
  ).withConverter(exerciseAnswerConverter);
}

function assertRuntimeTransition({
  currentStatus,
  transition,
}: {
  currentStatus: ExerciseRuntimeStatus;
  transition: ExerciseRuntimeTransition;
}): ExerciseRuntimeStatus {
  if (transition === "activate" && currentStatus === "not_opened") {
    return "active";
  }

  if (transition === "pass" && currentStatus === "active") {
    return "passed";
  }

  if (
    transition === "publish_correction" &&
    (currentStatus === "active" || currentStatus === "passed")
  ) {
    return "correction_visible";
  }

  throw new Error("Transition exercice non autorisée.");
}

export async function updateExerciseRuntimeStatus({
  actorUid,
  exercise,
  sessionId,
  transition,
}: {
  actorUid: string;
  exercise: SessionPublicExercise;
  sessionId: string;
  transition: ExerciseRuntimeTransition;
}): Promise<ExerciseRuntimeStatus> {
  const nextStatus = assertRuntimeTransition({
    currentStatus: exercise.runtimeStatus,
    transition,
  });
  const timestampField =
    nextStatus === "active"
      ? "activatedAt"
      : nextStatus === "passed"
        ? "passedAt"
        : "correctionVisibleAt";
  const nextCorrectionVisible = nextStatus === "correction_visible";
  const nextData = {
    correctionVisible: nextCorrectionVisible,
    runtimeStatus: nextStatus,
    [timestampField]: serverTimestamp(),
  };

  await Promise.all([
    updateDoc(
      sessionPublicExerciseRef({
        exerciseRuntimeId: exercise.id,
        sessionId,
      }),
      nextData,
    ),
    setDoc(
      sessionRuntimeExerciseRef({
        exerciseRuntimeId: exercise.id,
        sessionId,
      }),
      {
        correctionVisible: nextCorrectionVisible,
        id: exercise.id,
        sessionId,
        sourceExerciseId: exercise.sourceExerciseId,
        status: nextStatus,
        updatedAt: serverTimestamp(),
        versionId: exercise.versionId,
        [timestampField]: serverTimestamp(),
      },
      { merge: true },
    ),
  ]);

  await writeAuditEvent({
    actorUid,
    eventType: "session.exercise.status_updated",
    metadata: {
      exerciseRuntimeId: exercise.id,
      nextStatus,
      sessionId,
      transition,
    },
  });

  return nextStatus;
}

export async function listExerciseAnswersForSession(
  sessionId: string,
): Promise<ExerciseAnswerRecord[]> {
  if (!firestore) {
    return [];
  }

  const answersQuery = query(
    collection(
      firestore,
      SESSIONS_COLLECTION,
      sessionId,
      EXERCISE_ANSWERS_COLLECTION,
    ).withConverter(exerciseAnswerConverter),
    orderBy("updatedAt", "desc"),
  );
  const snapshot = await getDocs(answersQuery);
  return snapshot.docs.map((answerDoc) => answerDoc.data());
}

export async function readParticipantExerciseAnswer({
  exerciseRuntimeId,
  sessionId,
  uid,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
  uid: string;
}): Promise<ExerciseAnswerRecord | null> {
  const answerId = exerciseAnswerId({
    exerciseRuntimeId,
    uid,
  });
  const snapshot = await getDoc(exerciseAnswerRef({ answerId, sessionId }));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function saveParticipantExerciseAnswer({
  answerMarkdown,
  exercise,
  participantSlotId,
  sessionId,
  status,
  uid,
}: {
  answerMarkdown: string;
  exercise: SessionPublicExercise;
  participantSlotId: string;
  sessionId: string;
  status: ExerciseAnswerStatus;
  uid: string;
}): Promise<ExerciseAnswerRecord> {
  const currentAnswer = await readParticipantExerciseAnswer({
    exerciseRuntimeId: exercise.id,
    sessionId,
    uid,
  });

  if (currentAnswer?.status === "submitted") {
    throw new Error("Une réponse soumise n'est plus modifiable.");
  }

  const answerId = exerciseAnswerId({
    exerciseRuntimeId: exercise.id,
    uid,
  });
  const nextPayload = {
    answerMarkdown,
    exerciseRuntimeId: exercise.id,
    id: answerId,
    participantSlotId,
    sourceExerciseId: exercise.sourceExerciseId,
    startedAt: currentAnswer?.startedAt ?? serverTimestamp(),
    status,
    submittedAt: status === "submitted" ? serverTimestamp() : null,
    uid,
    updatedAt: serverTimestamp(),
    versionId: exercise.versionId,
  };

  await setDoc(exerciseAnswerRef({ answerId, sessionId }), nextPayload);

  const snapshot = await getDoc(exerciseAnswerRef({ answerId, sessionId }));
  const nextAnswer = snapshot.data();

  if (!nextAnswer) {
    throw new Error("Réponse exercice introuvable après sauvegarde.");
  }

  return nextAnswer;
}
